import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  LogOut,
  LayoutDashboard,
  User as UserIcon,
  ShieldCheck,
  CalendarDays,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteImage } from "@/hooks/useSiteImages";

const LOGO_API_DEFAULT =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_apicon-register/artifacts/1q137q9b_f04cf28b-14e3-4e14-b6c6-e1574fbf1d60.JPG";
const LOGO_ASSAM_DEFAULT =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_apicon-register/artifacts/ka43vom0_9012ac3f-853f-407b-aa81-8f5e96f3f583.JPG";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Committee", to: "/committee" },
  { label: "Blog", to: "/blog" },
  { label: "Gallery", to: "/gallery" },
];

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "AP";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const LOGO_API = useSiteImage("logoApi", LOGO_API_DEFAULT);
  const LOGO_ASSAM = useSiteImage("logoAssam", LOGO_ASSAM_DEFAULT);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header
      className="relative z-40 w-full bg-white shadow-[0_1px_0_rgba(11,61,122,0.06)]"
      data-testid="site-navbar"
    >
      {/* ============ DESKTOP: two-row layout ============ */}
      <div className="hidden md:block">
        {/* Top row — tall logo bar (scrolls away, not sticky) */}
        <div className="bg-white" data-testid="nav-logo-bar">
          <div className="border-b border-gray-100">
            <div className="mx-auto flex h-32 w-full max-w-7xl items-center justify-between px-8">
              <Link
                to="/"
                className="flex items-center gap-5"
                data-testid="nav-brand-link"
              >
                <img
                  src={LOGO_API}
                  alt="Association of Physicians of India"
                  className="h-[112px] w-[112px] shrink-0 rounded-2xl bg-white object-contain p-1.5 ring-1 ring-gray-200 shadow-sm"
                  data-testid="nav-logo-1"
                />
                <img
                  src={LOGO_ASSAM}
                  alt="API Assam Chapter"
                  className="h-[112px] w-[112px] shrink-0 rounded-2xl bg-white object-contain p-1.5 ring-1 ring-gray-200 shadow-sm"
                  data-testid="nav-logo-2"
                />
                <div className="ml-2 border-l border-gray-200 pl-5 leading-tight">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-apicon-gold">
                    35th Annual Conference
                  </div>
                  <div className="mt-1 font-display text-4xl font-bold tracking-tight text-apicon-navy">
                    Assam <span className="text-apicon-navy">Apicon</span>{" "}
                    <span className="text-apicon-gold">2026</span>
                  </div>
                  <div className="mt-1.5 text-[11px] font-medium leading-snug text-apicon-navy/80">
                    <span className="italic text-apicon-green">of</span>{" "}
                    Association of Physicians of India,
                    <br />
                    <span className="font-semibold uppercase tracking-[0.16em] text-apicon-green">
                      Assam State Chapter
                    </span>
                  </div>
                </div>
              </Link>

              {/* Right-side event info */}
              <div
                className="hidden text-right lg:block"
                data-testid="nav-event-info"
              >
                <div className="inline-flex flex-col gap-1 rounded-2xl border border-apicon-navy/10 bg-apicon-mist/50 px-5 py-3">
                  <span className="inline-flex items-center justify-end gap-2 text-sm text-apicon-navy">
                    <CalendarDays className="h-4 w-4 text-apicon-gold" />
                    <span className="font-semibold">
                      18 – 20 December 2026
                    </span>
                  </span>
                  <span className="inline-flex items-center justify-end gap-2 text-xs text-apicon-navy/80">
                    <MapPin className="h-3.5 w-3.5 text-apicon-gold" />
                    Tinsukia Medical College · Assam
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom row — sticky compact nav */}
        <div className="sticky top-0 z-50 border-b border-gray-100 bg-white/95 backdrop-blur-xl">
          <div className="mx-auto flex h-14 w-full max-w-7xl items-center justify-between px-8">
            {/* Compact brand — visible only when the tall logo bar has scrolled off */}
            <div
              className={cn(
                "flex items-center gap-2 transition-opacity duration-200",
                scrolled ? "opacity-100" : "opacity-0 pointer-events-none",
              )}
            >
              <img
                src={LOGO_API}
                alt=""
                aria-hidden
                className="h-8 w-8 rounded-lg bg-white object-contain ring-1 ring-gray-200"
              />
              <img
                src={LOGO_ASSAM}
                alt=""
                aria-hidden
                className="h-8 w-8 rounded-lg bg-white object-contain ring-1 ring-gray-200"
              />
              <Link
                to="/"
                className="ml-1 font-display text-lg font-bold tracking-tight text-apicon-navy"
                data-testid="nav-compact-brand"
              >
                Assam Apicon <span className="text-apicon-gold">2026</span>
              </Link>
            </div>

            <nav
              className="flex items-center gap-1"
              data-testid="nav-links"
            >
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    cn(
                      "relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200",
                      isActive
                        ? "text-apicon-navy"
                        : "text-gray-600 hover:text-apicon-navy",
                    )
                  }
                >
                  {({ isActive }) => (
                    <span className="relative">
                      {link.label}
                      <span
                        className={cn(
                          "absolute -bottom-1 left-1/2 h-[2px] w-0 -translate-x-1/2 rounded-full bg-apicon-gold transition-[width] duration-300",
                          isActive && "w-6",
                        )}
                      />
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-2 rounded-full border border-gray-200 bg-white/80 py-1 pl-1 pr-4 transition-colors hover:border-apicon-navy/30 hover:bg-apicon-mist"
                      data-testid="nav-user-menu-trigger"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-apicon-navy text-[11px] font-semibold text-white">
                        {initials(user?.name)}
                      </span>
                      <span className="text-sm font-medium text-apicon-navy">
                        {user?.name?.split(" ")[0] || "Delegate"}
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-56"
                    data-testid="nav-user-menu"
                  >
                    <DropdownMenuLabel className="text-xs uppercase tracking-[0.18em] text-gray-500">
                      Signed in as
                    </DropdownMenuLabel>
                    <div className="px-2 pb-2 text-sm">
                      <div className="truncate font-medium text-apicon-navy">
                        {user?.name}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        {user?.email}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate("/dashboard")}
                      data-testid="nav-user-menu-dashboard"
                    >
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/payment")}
                      data-testid="nav-user-menu-payment"
                    >
                      <UserIcon className="mr-2 h-4 w-4" />
                      Payment
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem
                        onClick={() => navigate("/admin")}
                        data-testid="nav-user-menu-admin"
                      >
                        <ShieldCheck className="mr-2 h-4 w-4 text-apicon-gold" />
                        Admin console
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600 focus:text-red-600"
                      data-testid="nav-user-menu-logout"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login" data-testid="nav-login-link">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full px-4 text-apicon-navy hover:bg-apicon-mist hover:text-apicon-navy"
                    >
                      Sign in
                    </Button>
                  </Link>
                  <Link to="/register" data-testid="nav-register-link">
                    <Button
                      size="sm"
                      className="rounded-full bg-apicon-gold px-5 font-semibold text-apicon-navy shadow-sm shadow-apicon-gold/40 hover:bg-[#ffb400] hover:text-apicon-navy"
                    >
                      Register
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============ MOBILE: single compact bar ============ */}
      <div className="border-b border-gray-100 md:hidden">
        <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-5">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="nav-brand-link-mobile"
          >
            <img
              src={LOGO_API}
              alt="API"
              className="h-12 w-12 shrink-0 rounded-xl bg-white object-contain p-0.5 ring-1 ring-gray-200"
            />
            <img
              src={LOGO_ASSAM}
              alt="API Assam Chapter"
              className="h-12 w-12 shrink-0 rounded-xl bg-white object-contain p-0.5 ring-1 ring-gray-200"
            />
            <div className="ml-1 leading-tight">
              <div className="font-display text-base font-bold tracking-tight text-apicon-navy">
                Assam Apicon <span className="text-apicon-gold">2026</span>
              </div>
              <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-apicon-green">
                Assam State Chapter · Tinsukia
              </div>
            </div>
          </Link>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white/80 text-apicon-navy"
            aria-label="Toggle navigation"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div
            className="bg-white/95 backdrop-blur-xl"
            data-testid="nav-mobile-menu"
          >
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    cn(
                      "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-apicon-mist text-apicon-navy"
                        : "text-gray-700 hover:bg-gray-50",
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}

              {isAuthenticated ? (
                <div className="mt-3 space-y-2">
                  <div className="rounded-xl bg-apicon-mist/70 p-3 text-sm">
                    <div className="font-medium text-apicon-navy">
                      {user?.name}
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      {user?.email}
                    </div>
                  </div>
                  <Link to="/dashboard" data-testid="nav-mobile-dashboard">
                    <Button
                      variant="outline"
                      className="w-full rounded-full border-apicon-navy/20 text-apicon-navy"
                    >
                      Dashboard
                    </Button>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" data-testid="nav-mobile-admin">
                      <Button
                        variant="outline"
                        className="w-full rounded-full border-apicon-navy/20 text-apicon-navy"
                      >
                        Admin console
                      </Button>
                    </Link>
                  )}
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="w-full rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="nav-mobile-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </Button>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Link to="/login" data-testid="nav-mobile-login">
                    <Button
                      variant="outline"
                      className="w-full rounded-full border-apicon-navy/20 text-apicon-navy"
                    >
                      Sign in
                    </Button>
                  </Link>
                  <Link to="/register" data-testid="nav-mobile-register">
                    <Button className="w-full rounded-full bg-apicon-gold font-semibold text-apicon-navy hover:bg-[#ffb400]">
                      Register
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
